/**
 * 낭만궁합 API 응답 → 컴포넌트 hData 변환기
 *
 * API 응답 구조와 UI 컴포넌트가 기대하는 데이터 구조가 다르므로
 * 이 변환기를 통해 API 응답을 hData 형식으로 매핑합니다.
 */

import { getCharacterImagesForPersons } from '@/components/saju-insight/nangman-harmony/constants'

/**
 * 콘텐츠 텍스트 포맷팅
 * - | → <br> (줄바꿈)
 * - **텍스트** → <strong>텍스트</strong> (볼드)
 */
export const formatContent = (text) => {
  if (!text || typeof text !== 'string') return text
  return text
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replaceAll('|', '<br>')
}

/**
 * 배열 내 문자열 항목에 포맷팅 적용
 */
export const formatContentArray = (arr) => {
  if (!Array.isArray(arr)) return arr
  return arr.map((item) => (typeof item === 'string' ? formatContent(item) : item))
}

/** API category → 응답 키 매핑 */
const CATEGORY_KEY_MAP = {
  LOVER: '연인',
  FRIEND: '친구',
  BUSINESS: '비즈니스',
  FAMILY: '가족',
}

/** 관계 타입 한글 → 영문 매핑 */
const RELATION_TYPE_MAP = {
  생: 'saeng',
  극: 'geuk',
  비견: 'bigyeon',
}

/** node ID에서 person 인덱스 추출 (a→0, b→1, c→2, d→3) */
const getPersonIndex = (nodeId) => {
  const prefix = nodeId?.charAt(0)
  const map = { a: 0, b: 1, c: 2, d: 3 }
  return map[prefix] ?? 0
}

/**
 * 나의스타일/상대스타일 → persons 배열 항목으로 변환 (이미지 제외)
 */
const transformPerson = (style) => {
  if (!style) return null
  return {
    name: style.이름,
    gender: style.성별,
    age: style.나이,
    oheng: style.일간,
    ilgan: style.일간,
    ilganHanja: '',
    ganji: style.일주,
    ganjiHanja: style.일주한자,
    birthInfo: style.시간 ? `${style.생년월일} ${style.시간}` : style.생년월일,
    desc: formatContent(style.오행설명),
    summary: formatContent(style.한줄특성),
    traits: formatContentArray(style.대인관계 || []),
    keywords: style.키워드 || [],
    _rawBirth: style.생년월일,
    _rawTime: style.시간,
  }
}

/** persons 배열에 중복 없는 캐릭터 이미지 부여 */
const assignCharacterImages = (persons, rawStyles) => {
  const infos = persons.map((p, i) => {
    const raw = rawStyles[i] || {}
    const genderLabel = raw.성별 === 'MEN' ? '남' : raw.성별 === 'WOMEN' ? '여' : raw.성별
    return {
      gender: genderLabel,
      age: raw.나이,
      key: `${raw.이름 || ''}-${raw.생년월일 || ''}-${raw.시간 || ''}`,
    }
  })
  const images = getCharacterImagesForPersons(infos)
  persons.forEach((p, i) => {
    p.characterImg = images[i]
    delete p._rawBirth
    delete p._rawTime
  })
}

/**
 * 오행관계도 edges → relations 배열 변환
 * ilgan→ilgan 관계만 필터링
 */
const transformRelations = (edges) => {
  if (!edges?.length) return []
  return edges
    .filter((e) => e.from?.includes('ilgan') && e.to?.includes('ilgan'))
    .map((e) => ({
      from: getPersonIndex(e.from),
      to: getPersonIndex(e.to),
      type: RELATION_TYPE_MAP[e.type] || 'saeng',
    }))
}

/**
 * 항목별궁합 → scoreItems 변환
 */
const transformScoreItems = (items) => {
  if (!items?.length) return undefined
  return items.map((item) => ({
    label: item.지표명 || '',
    score: item.백분율 || 0,
  }))
}

/**
 * 2_관계타입 → 관계타입 객체 변환
 */
const transformRelationType = (summary, relType, overallSummary) => {
  if (!summary && !relType) return undefined
  return {
    score: summary?.백분율 ?? 0,
    level: summary?.등급 ?? '',
    gaugeDescription: formatContent(summary?.종합코멘트 ?? ''),
    typeName: formatContent(relType?.관계타입 ?? ''),
    subtitle: formatContent(relType?.타입소개 ?? ''),
    description: formatContent(relType?.타입설명 ?? ''),
    badge: formatContent(overallSummary ?? ''),
  }
}

/**
 * 2_관계타입 → 관계분석카드 배열 변환
 */
const transformRelationCards = (relType) => {
  if (!relType) return undefined
  const cards = []
  if (relType.관계흐름) {
    cards.push({
      id: 'flow',
      content: formatContentArray(Array.isArray(relType.관계흐름) ? relType.관계흐름 : [relType.관계흐름]),
    })
  }
  if (relType.잘맞는순간) {
    cards.push({
      id: 'goodMoments',
      content: formatContentArray(Array.isArray(relType.잘맞는순간) ? relType.잘맞는순간 : [relType.잘맞는순간]),
    })
  }
  if (relType.장점) {
    cards.push({
      id: 'strengths',
      content: formatContentArray(Array.isArray(relType.장점) ? relType.장점 : [relType.장점]),
    })
  }
  if (relType.주의점) {
    cards.push({
      id: 'cautions',
      content: formatContentArray(Array.isArray(relType.주의점) ? relType.주의점 : [relType.주의점]),
    })
  }
  if (relType.갈등패턴) {
    cards.push({
      id: 'conflictPattern',
      content: formatContentArray(Array.isArray(relType.갈등패턴) ? relType.갈등패턴 : [relType.갈등패턴]),
    })
  }
  return cards
}

/**
 * 3_지표상세 → 지표상세분석 / 관계스타일분석 변환
 * 앞 2개 → 지표상세분석, 뒤 3개 → 관계스타일분석
 */
const transformInterpretBranch = (branch) => {
  if (!branch || typeof branch !== 'object') return null
  const intro = formatContent(branch.도입부 || '')
  const situation = formatContent(branch.상황판단 || '')
  const action = formatContent(branch.행동권유 || '')
  if (!intro && !situation && !action) return null
  return { intro, situation, action }
}

const transformInterpretPairs = (detail) => {
  if (!detail || !Array.isArray(detail.개별궁합)) return []
  return detail.개별궁합
    .map((p) => {
      const intro = formatContent(p.도입부 || '')
      const situation = formatContent(p.상황판단 || '')
      const action = formatContent(p.행동권유 || '')
      if (!intro && !situation && !action) return null
      return {
        intro,
        situation,
        action,
        badge: p.배지 || '',
        score: p.백분율 ?? 0,
        pair: p.쌍 || '',
      }
    })
    .filter(Boolean)
}

const transformInterpretDetail = (detail) => {
  if (!detail || typeof detail !== 'object') return null
  const best = transformInterpretBranch(detail.최고)
  const worst = transformInterpretBranch(detail.최저)
  const overall = formatContent(detail.종합 || '')
  const pairs = transformInterpretPairs(detail)
  if (!best && !worst && !overall && !pairs.length) return null
  return { overall, best, worst, pairs }
}

const transformIndicator = (item, index) => ({
  id: `indicator_${index}`,
  iconIndex: index,
  title: item.표시명 || item.지표명 || '',
  rating: item.점수 ?? 4,
  mainDesc: formatContent(item.해석 || ''),
  subDesc: formatContent(item.설명 || ''),
  situation: formatContent(item['이런 이유예요'] || item.근거 || ''),
  practices: formatContentArray(item['함께 해볼 것들'] || item.실천방법 || []),
  tags: item.키워드 || [],
  bestPair: item.최고쌍 ? { name: item.최고쌍.이름 || '', score: item.최고쌍.백분율 ?? 0 } : null,
  worstPair: item.최저쌍 ? { name: item.최저쌍.이름 || '', score: item.최저쌍.백분율 ?? 0 } : null,
  detailInterpretation: transformInterpretDetail(item.해석상세),
})

/**
 * 4_종합조언 → 종합조언 변환
 */
const transformAdvice = (advice) => {
  if (!advice) return undefined
  return {
    summary: formatContent(advice.종합 || ''),
    strengths: formatContentArray(advice.장점 || []),
    cautions: formatContentArray(advice.주의점 || []),
    tips: formatContentArray(advice.조언 || []),
  }
}

/**
 * 5_활동추천 → 함께하면좋은활동 배열 변환
 */
const transformActivities = (activities) => {
  if (!activities) return undefined
  return Object.values(activities).map((act) => ({
    oheng: act.fe || '',
    title: formatContent(act.label || ''),
    desc: formatContent(act.근거 || ''),
    keywords: act.items || [],
  }))
}

/**
 * 메인 변환 함수
 * @param {Object} response - API 응답 (response.data)
 * @param {string} category - API 카테고리 (LOVER/FRIEND/BUSINESS/FAMILY)
 * @returns {Object} 컴포넌트용 hData 객체
 */
export const transformHarmonyResponse = (response, category) => {
  if (!response) return {}

  const categoryKey = CATEGORY_KEY_MAP[category] || '연인'
  const categoryData = response[categoryKey]
  const 기본정보 = response.기본정보
  const 종합 = response.종합

  const summary = categoryData?.['1_종합요약']
  const relType = categoryData?.['2_관계타입']
  const indicators = categoryData?.['3_지표상세']
  const advice = categoryData?.['4_종합조언']
  const activities = categoryData?.['5_활동추천']

  // persons: 인원별스타일 배열에서 변환
  const rawStyles = summary?.인원별스타일 || []
  const persons = rawStyles.map(transformPerson).filter(Boolean)
  assignCharacterImages(persons, rawStyles)

  // 오행관계도 nodes에서 ilgan 정보 추출하여 persons에 매핑
  // label: 한글 읽기 (계, 병 등), hanja: 한자 (癸, 丙 등), ohaeng: 오행 (수, 화 등)
  const nodes = 기본정보?.오행관계도?.nodes || []
  const personKeys = ['A', 'B', 'C', 'D']
  persons.forEach((p, i) => {
    const ilganNode = nodes.find((n) => n.person === personKeys[i] && n.role === '일간')
    if (ilganNode) {
      p.ilganLabel = ilganNode.label || '' // 한글 읽기: 계, 병, 을 등
      p.ilganHanja = ilganNode.hanja || '' // 한자: 癸, 丙, 乙 등
      if (ilganNode.ohaeng) p.oheng = ilganNode.ohaeng
    }
  })

  // relations: 오행관계도 edges (ilgan→ilgan만)
  const relations = transformRelations(기본정보?.오행관계도?.edges)

  // 지표 분리: 앞 2개 → 지표상세분석, 뒤 3개 → 관계스타일분석
  const allIndicators = indicators?.map(transformIndicator) || []
  const 지표상세분석 = allIndicators.slice(0, 2)
  const 관계스타일분석 = allIndicators.slice(2, 5).map((item, i) => ({
    ...item,
    iconIndex: i + 2,
  }))

  // 역할구분: 나의역할 + 상대별역할(배열, 1~3명) / 상대마다 관계메시지
  const 역할구분Raw = summary?.역할구분
  const 상대별역할Raw = Array.isArray(역할구분Raw?.상대별역할)
    ? 역할구분Raw.상대별역할
    : 역할구분Raw?.상대역할
      ? [역할구분Raw.상대역할]
      : []

  const pickFunctions = (설명) => {
    if (설명 && typeof 설명 === 'object') {
      return {
        위치: formatContent(설명.위치 || ''),
        기여: formatContent(설명.기여 || ''),
        유의: formatContent(설명.유의 || ''),
      }
    }
    return null
  }

  const 역할구분 = 역할구분Raw
    ? {
        mine: 역할구분Raw.나의역할
          ? {
              name: 역할구분Raw.나의역할.이름 || '',
              type: 역할구분Raw.나의역할.역할 || 역할구분Raw.나의역할.타입 || '',
              desc: formatContent(
                typeof 역할구분Raw.나의역할.역할설명 === 'string'
                  ? 역할구분Raw.나의역할.역할설명
                  : 역할구분Raw.나의역할.설명 || '',
              ),
              functions: pickFunctions(역할구분Raw.나의역할.역할설명),
              reason: formatContent(역할구분Raw.나의역할.사주근거 || 역할구분Raw.나의역할.근거 || ''),
            }
          : null,
        partners: 상대별역할Raw.map((p) => ({
          name: p.이름 || '',
          type: p.역할 || p.역할타입 || p.타입 || '',
          desc: formatContent(typeof p.역할설명 === 'string' ? p.역할설명 : p.설명 || ''),
          functions: pickFunctions(p.역할설명),
          reason: formatContent(p.사주근거 || p.근거 || ''),
          summary: formatContent(
            p.관계메시지 ||
              p.조합메시지 ||
              역할구분Raw.관계메시지 ||
              역할구분Raw.조합메시지 ||
              '',
          ),
        })),
      }
    : undefined

  return {
    한줄요약: formatContent(종합?.종합코멘트 || ''),
    persons,
    relations,
    항목별궁합: transformScoreItems(summary?.항목별궁합),
    역할구분,
    관계타입: transformRelationType(summary, relType, 종합?.쌍방요약),
    관계분석카드: transformRelationCards(relType),
    지표상세분석: 지표상세분석.length ? 지표상세분석 : undefined,
    관계스타일분석: 관계스타일분석.length ? 관계스타일분석 : undefined,
    종합조언: transformAdvice(advice),
    함께하면좋은활동: transformActivities(activities),
    // 원본 데이터 보존 (디버깅/추후 확장용)
    _raw종합: 종합,
    _rawCategory: categoryData,
  }
}
